import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Table, Tag, Button, Spin, Empty, Space, Typography, Card
} from 'antd';
import {
    ArrowLeftOutlined,
    UserOutlined,
    BarChartOutlined,
    DownloadOutlined,
    FileOutlined,
} from '@ant-design/icons';
import { capturePageAsHTML } from '../utils/exportHTML';
import dayjs from 'dayjs';
import { getCourse, getCourseTopLearners } from '../services/api';

const { Title, Text } = Typography;

const numSorter = (key) => (a, b) => (Number(a[key]) || 0) - (Number(b[key]) || 0);
const tagRender = (color) => (v) => v > 0 ? <Tag color={color}>{v}</Tag> : '-';
const dateRender = (v) => v ? dayjs(v).format('YYYY-MM-DD') : '-';

export default function CourseLearnersPage() {
    const { courseKey } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [course, setCourse] = useState(null);
    const [learners, setLearners] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [courseRes, learnersRes] = await Promise.all([
                    getCourse(courseKey),
                    getCourseTopLearners(courseKey, 500)
                ]);
                setCourse(courseRes.data.data);
                setLearners(learnersRes.data.data || []);
            } catch (error) {
                console.error('Failed to fetch learners', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [courseKey]);

    const exportCSV = () => {
        if (!learners?.length) return;
        const csvColumns = [
            ['Username', 'username'],
            ['First Activity', 'first_activity'],
            ['Last Activity', 'last_activity'],
            ['Active Days', 'active_days'],
            ['Enroll', 'enroll_activate'],
            ['Unenroll', 'enroll_deactivate'],
            ['Video Play', 'video_play'],
            ['Video Pause', 'video_pause'],
            ['Video Seek', 'video_seek'],
            ['Video Stop', 'video_stop'],
            ['Video Speed', 'video_speed'],
            ['Unique Videos', 'unique_videos'],
            ['Transcript', 'video_transcript'],
            ['CC', 'video_cc'],
            ['Video Load', 'video_load'],
            ['Problem Check', 'prob_check'],
            ['Problem Save', 'prob_save'],
            ['Problem Show', 'prob_show'],
            ['Unique Problems', 'unique_problems'],
            ['Hint', 'prob_hint'],
            ['Show Answer', 'prob_answer'],
            ['Problem Reset', 'prob_reset'],
            ['Block Complete', 'block_complete'],
            ['Grade Event', 'grade_event'],
            ['Cert Earned', 'cert_earned'],
            ['Cert Shared', 'cert_shared'],
            ['Forum Post', 'forum_post'],
            ['Forum View', 'forum_view'],
            ['Forum Vote', 'forum_vote'],
            ['Forum Search', 'forum_search'],
            ['Forum Edit', 'forum_update'],
            ['Nav Step', 'nav_step'],
            ['Nav Tab', 'nav_tab'],
            ['Nav Jump', 'nav_jump'],
            ['Course Views', 'course_view'],
            ['Dash Clicks', 'dash_clicks'],
            ['Progress View', 'progress_view'],
            ['Total Events', 'total_events'],
        ];
        const header = csvColumns.map(([label]) => label).join(',');
        const rows = learners.map(row =>
            csvColumns.map(([, key]) => {
                const val = row[key] ?? '';
                if (key === 'first_activity' || key === 'last_activity') {
                    return val ? dayjs(val).format('YYYY-MM-DD') : '';
                }
                return String(val).includes(',') ? `"${val}"` : val;
            }).join(',')
        );
        const csv = [header, ...rows].join('\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `learners_${course.display_name || course.course_id}_${dayjs().format('YYYYMMDD')}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    if (loading) return <div style={{ textAlign: 'center', padding: '100px' }}><Spin size="large" /></div>;
    if (!course) return <Empty description="Data not found" />;

    const learnerColumns = [
        {
            title: 'Username',
            dataIndex: 'username',
            key: 'username',
            fixed: 'left',
            width: 150,
            render: (text) => <Space><UserOutlined /> {text || 'Anonymous'}</Space>
        },
        {
            title: 'Activity',
            children: [
                { title: 'First', dataIndex: 'first_activity', width: 110, sorter: numSorter('first_activity'), render: dateRender },
                { title: 'Last', dataIndex: 'last_activity', width: 110, sorter: numSorter('last_activity'), render: dateRender },
                { title: 'Days', dataIndex: 'active_days', width: 70, sorter: numSorter('active_days'), render: tagRender('default') },
            ]
        },
        {
            title: 'Enrollment',
            children: [
                { title: 'Enroll', dataIndex: 'enroll_activate', width: 80, sorter: numSorter('enroll_activate'), render: tagRender('green') },
                { title: 'Unenroll', dataIndex: 'enroll_deactivate', width: 80, sorter: numSorter('enroll_deactivate'), render: tagRender('red') },
            ]
        },
        {
            title: 'Video Interactions',
            children: [
                {
                    title: 'Playback',
                    children: [
                        { title: 'Play', dataIndex: 'video_play', width: 80, sorter: numSorter('video_play'), render: tagRender('blue') },
                        { title: 'Pause', dataIndex: 'video_pause', width: 80, sorter: numSorter('video_pause'), render: tagRender('blue') },
                        { title: 'Seek', dataIndex: 'video_seek', width: 80, sorter: numSorter('video_seek'), render: tagRender('cyan') },
                        { title: 'Stop', dataIndex: 'video_stop', width: 80, sorter: numSorter('video_stop'), render: tagRender('blue') },
                        { title: 'Spd', dataIndex: 'video_speed', width: 80, sorter: numSorter('video_speed'), render: tagRender('blue') },
                        { title: 'Uniq', dataIndex: 'unique_videos', width: 80, sorter: numSorter('unique_videos'), render: tagRender('geekblue') },
                    ]
                },
                {
                    title: 'Content',
                    children: [
                        { title: 'Trans', dataIndex: 'video_transcript', width: 80, sorter: numSorter('video_transcript'), render: tagRender('geekblue') },
                        { title: 'CC', dataIndex: 'video_cc', width: 80, sorter: numSorter('video_cc'), render: tagRender('geekblue') },
                        { title: 'Load', dataIndex: 'video_load', width: 80, sorter: numSorter('video_load'), render: tagRender('geekblue') },
                    ]
                }
            ]
        },
        {
            title: 'Problem Interactions',
            children: [
                {
                    title: 'Submission',
                    children: [
                        { title: 'Check', dataIndex: 'prob_check', width: 80, sorter: numSorter('prob_check'), render: tagRender('green') },
                        { title: 'Save', dataIndex: 'prob_save', width: 80, sorter: numSorter('prob_save'), render: tagRender('green') },
                        { title: 'Show', dataIndex: 'prob_show', width: 80, sorter: numSorter('prob_show'), render: tagRender('green') },
                        { title: 'Uniq', dataIndex: 'unique_problems', width: 80, sorter: numSorter('unique_problems'), render: tagRender('lime') },
                    ]
                },
                {
                    title: 'Assistance',
                    children: [
                        { title: 'Hint', dataIndex: 'prob_hint', width: 80, sorter: numSorter('prob_hint'), render: tagRender('orange') },
                        { title: 'Ans', dataIndex: 'prob_answer', width: 80, sorter: numSorter('prob_answer'), render: tagRender('volcano') },
                        { title: 'Reset', dataIndex: 'prob_reset', width: 80, sorter: numSorter('prob_reset'), render: tagRender('orange') },
                    ]
                }
            ]
        },
        {
            title: 'Completion / Certificate',
            children: [
                { title: 'Block', dataIndex: 'block_complete', width: 80, sorter: numSorter('block_complete'), render: tagRender('cyan') },
                { title: 'Grade', dataIndex: 'grade_event', width: 80, sorter: numSorter('grade_event'), render: tagRender('green') },
                { title: 'Cert', dataIndex: 'cert_earned', width: 80, sorter: numSorter('cert_earned'), render: tagRender('gold') },
                { title: 'Share', dataIndex: 'cert_shared', width: 80, sorter: numSorter('cert_shared'), render: tagRender('gold') },
            ]
        },
        {
            title: 'Discussion Forum',
            children: [
                { title: 'Post', dataIndex: 'forum_post', width: 80, sorter: numSorter('forum_post'), render: tagRender('purple') },
                { title: 'View', dataIndex: 'forum_view', width: 80, sorter: numSorter('forum_view'), render: tagRender('purple') },
                { title: 'Vote', dataIndex: 'forum_vote', width: 80, sorter: numSorter('forum_vote'), render: tagRender('magenta') },
                { title: 'Search', dataIndex: 'forum_search', width: 80, sorter: numSorter('forum_search'), render: tagRender('magenta') },
                { title: 'Edit', dataIndex: 'forum_update', width: 80, sorter: numSorter('forum_update'), render: tagRender('purple') },
            ]
        },
        {
            title: 'Navigation',
            children: [
                { title: 'Step', dataIndex: 'nav_step', width: 80, sorter: numSorter('nav_step'), render: tagRender('pink') },
                { title: 'Tab', dataIndex: 'nav_tab', width: 80, sorter: numSorter('nav_tab'), render: tagRender('pink') },
                { title: 'Jump', dataIndex: 'nav_jump', width: 80, sorter: numSorter('nav_jump'), render: tagRender('pink') },
            ]
        },
        {
            title: 'Course Views',
            dataIndex: 'course_view',
            width: 100,
            sorter: numSorter('course_view'),
            render: tagRender('default')
        },
        {
            title: 'Dash Click',
            dataIndex: 'dash_clicks',
            width: 100,
            sorter: numSorter('dash_clicks'),
            render: tagRender('gold')
        },
        {
            title: 'Progress',
            dataIndex: 'progress_view',
            width: 100,
            sorter: numSorter('progress_view'),
            render: tagRender('lime')
        },
        {
            title: 'Total',
            dataIndex: 'total_events',
            key: 'total_events',
            width: 100,
            fixed: 'right',
            sorter: numSorter('total_events'),
            render: (val) => <Text strong>{parseInt(val).toLocaleString()}</Text>
        }
    ];

    return (
        <div style={{ paddingBottom: 40 }}>
            <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(`/courses/${courseKey}/analytics`)}
                style={{ marginBottom: 24 }}
                data-export-hide
            >
                Back to Analytics
            </Button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                <div>
                    <Title level={2} style={{ margin: 0 }}>
                        <BarChartOutlined /> Learner Interaction Details
                    </Title>
                    <Text type="secondary">
                        Exhaustive breakdown of all student interactions for <b>{course.display_name || course.course_id}</b>
                    </Text>
                </div>
                <Space data-export-hide>
                    <Button
                        icon={<FileOutlined />}
                        onClick={() => capturePageAsHTML(`learners_${course.display_name || course.course_id}`)}
                        disabled={!learners.length}
                    >
                        Export HTML
                    </Button>
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={exportCSV}
                        type="primary"
                        disabled={!learners.length}
                    >
                        Export CSV
                    </Button>
                </Space>
            </div>

            <Card bordered={false} bodyStyle={{ padding: 0 }}>
                <Table
                    dataSource={learners}
                    columns={learnerColumns}
                    rowKey="username"
                    pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
                    scroll={{ x: 'max-content' }}
                    bordered
                    size="middle"
                />
            </Card>

            <style dangerouslySetInnerHTML={{
                __html: `
                .ant-table-thead > tr > th {
                    text-align: center !important;
                }
            `}} />
        </div>
    );
}
